/* eslint-disable @typescript-eslint/naming-convention */
import {
  JSX, useCallback, useMemo,
} from 'react';
import {
  Box, Checkbox, SxProps, Theme,
  Tooltip,
} from '@mui/material';
import {
  InventoryStatus, ISubmitAssayData, lbAssays,
} from '@zero-dash/types';
import dayjs from 'dayjs';
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { Highlight } from '@mantine/core';
import CustomChip from '@/components/Common/Chip';
import CustomTypography from '@/components/Common/Typography';
import { corePalette } from '@/themes/colours';
import CustomDataGrid from '@/components/Common/DataGrid';
import { FilesButton } from '@/components/Common/FilesButton';
import { AssaysOrderedCell } from './AssaysOrderedCell';
import { BiomaterialIdCell } from './BiomaterialIdCell';
import { IBiomaterialRow } from '@/types/PatientDashboard/TimelineView.types';

interface IProps {
  rows: IBiomaterialRow[];
  selectedBiomaterialIds: number[];
  dataToSubmit: ISubmitAssayData[];
  onToggle: (biomaterialId: number, checked: boolean) => void;
  canSelectBiomaterial: boolean;
  search?: string[];
}

const specimenPurposeToAbbr = {
  'Germline - donor': 'GD',
  'Germline - patient': 'GP',
  'Liquid Biopsy': 'LB',
  'TB Banked': 'TBB',
  'TB ZERO Potentials': 'TBZ',
  Tumour: 'TT',
};

const otherStatusesColours = {
  colour: corePalette.blue300,
  backgroundColour: corePalette.blue30,
};

const inventoryStatusMapToColour: Record<
  InventoryStatus,
  { colour: string; backgroundColour: string }
> = {
  'In Inventory': {
    colour: corePalette.green300,
    backgroundColour: corePalette.green10,
  },
  Exhausted: {
    colour: corePalette.grey200,
    backgroundColour: corePalette.grey30,
  },
  Destroyed: {
    colour: corePalette.red200,
    backgroundColour: corePalette.red10,
  },
  Transferred: otherStatusesColours,
  Lost: otherStatusesColours,
  Reserved: otherStatusesColours,
  'For aliquoting': otherStatusesColours,
  'In Transit': otherStatusesColours,
};

export default function BiomaterialsTable({
  rows,
  selectedBiomaterialIds,
  dataToSubmit,
  onToggle,
  canSelectBiomaterial,
  search,
}: IProps): JSX.Element {
  const rowMatchesSelected = (
    row: GridValidRowModel,
  ): boolean => dataToSubmit.some((d) => d.biomaterialId === row.id);

  const renderAssayChip = useCallback((
    assay: string,
    row: IBiomaterialRow,
    sx?: SxProps<Theme>,
  ): JSX.Element => {
    const label = (lbAssays[assay] ?? assay).toUpperCase();

    return (
      <CustomChip
        key={`${row.name}::${row.id}::${assay}`}
        label={label}
        colour={corePalette.grey200}
        backgroundColour={corePalette.grey30}
        sx={sx}
      />
    );
  }, []);

  const cols: GridColDef<IBiomaterialRow>[] = useMemo(() => [
    {
      field: 'selected',
      flex: 0,
      width: 45,
      minWidth: 45,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      headerName: '',
      headerClassName: 'no-right-separator',
      renderHeader: () => null,
      renderCell: ({ row }) => {
        const disabledReasons = {
          'Assays already ordered for this biomaterial': row.qlimsId,
          'Downstream process assigned': row.downstreamProcess,
          'Biomaterial is exahusted': ['Exhausted'].includes(row.inventoryStatus),
          'Biomaterial is destroyed': ['Destroyed'].includes(row.inventoryStatus),
        };
        const tooltipText = Object.entries(disabledReasons)
          .map(([k, v]) => (v ? k : ''))
          .filter((reason) => Boolean(reason))
          .join('\n');

        return canSelectBiomaterial ? (
          <Tooltip
            title={tooltipText}
            placement="right"
            disableFocusListener={!tooltipText}
            disableHoverListener={!tooltipText}
            disableTouchListener={!tooltipText}
          >
            <Box width="100%" display="flex" justifyContent="center">
              <Checkbox
                checked={
                  selectedBiomaterialIds.includes(row.id)
                  || dataToSubmit.some((d) => d.biomaterialId === row.id)
                }
                onChange={(e, checked): void => {
                  onToggle(row.id, checked);
                }}
                disabled={Object.values(disabledReasons).some((v) => v)}
              />
            </Box>
          </Tooltip>
        ) : <div />;
      },
    },
    {
      field: 'name',
      headerName: 'Biomaterial name',
      minWidth: 200,
      flex: 3,
      renderCell: ({ row }) => {
        const specimenPurpose = row.specimenPurpose || '';
        const abbrevSpecimenPurpose = specimenPurposeToAbbr[specimenPurpose];

        return (
          <Box width="100%" display="flex" gap="8px">
            <Box
              display="flex"
              width="calc(100% - 40px)"
            >
              <CustomTypography variant="bodyRegular" fontWeight="medium" truncate>
                <Highlight
                  highlightStyles={{ backgroundColor: corePalette.green30 }}
                  highlight={search ?? []}
                  component="span"
                >
                  {row.name}
                </Highlight>
              </CustomTypography>
            </Box>
            <Box width="30px">
              {abbrevSpecimenPurpose && (
                <CustomChip
                  label={abbrevSpecimenPurpose}
                  colour={corePalette.white}
                  backgroundColour={corePalette.offBlack100}
                  tooltipText={(<CustomTypography variant="bodyRegular">{specimenPurpose}</CustomTypography>)}
                />
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'id',
      headerName: 'Biomaterial ID',
      minWidth: 125,
      flex: 1,
      renderCell: ({ row }) => (
        <BiomaterialIdCell row={row} search={search} />
      ),
    },
    {
      field: 'patientId',
      headerName: 'Patient ID',
      minWidth: 125,
      flex: 1,
    },
    {
      field: 'subtype',
      type: 'string',
      headerName: 'Subtype',
      minWidth: 150,
      flex: 2,
      renderCell: ({ row }) => (
        <CustomChip
          label={row.subtype?.toUpperCase() || 'NOT SET'}
          backgroundColour={corePalette.grey30}
        />
      ),
    },
    {
      field: 'collectionDate',
      headerName: 'Sample collection date',
      type: 'date',
      minWidth: 200,
      flex: 2,
      sortable: false,
      valueGetter: (value, row) => {
        const d = row.collectionDate?.date;
        if (!d) return null;
        const parsed = new Date(d);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      },
      renderCell: ({ row }) => {
        const { date, daysFrom } = row.collectionDate;

        return (
          <Box display="flex" gap="8px">
            <CustomTypography fontWeight="medium" variant="bodyRegular" truncate>
              {`Day: ${daysFrom}`}
            </CustomTypography>
            <CustomChip
              label={dayjs(date).format('DD/MM/YYYY')}
              fontWeight="bold"
              backgroundColour={corePalette.grey10}
              border={`1px solid ${corePalette.grey50}`}
            />
          </Box>
        );
      },
    },
    {
      field: 'inventoryStatus',
      type: 'string',
      headerName: 'Inventory Status',
      minWidth: 150,
      flex: 2,
      renderCell: ({ row }) => (
        <CustomChip
          label={row.inventoryStatus.toUpperCase() || 'NOT SET'}
          backgroundColour={inventoryStatusMapToColour[row.inventoryStatus]?.backgroundColour}
          colour={inventoryStatusMapToColour[row.inventoryStatus]?.colour}
        />
      ),
    },
    {
      field: 'volume',
      type: 'string',
      headerName: 'Volume',
      minWidth: 100,
      renderCell: ({ row }) => {
        if (!row.volume) return row.volume;
        const [num, unit] = row.volume.split(' ');
        const parsed = parseFloat(num);
        const formatted = Number.isNaN(parsed) ? row.volume : `${parsed.toFixed(2)} ${unit}`;
        return <CustomTypography variant="bodyRegular" fontWeight="medium">{formatted}</CustomTypography>;
      },
    },
    {
      field: 'containerType',
      type: 'string',
      headerName: 'Container type',
      minWidth: 150,
      flex: 2,
    },
    {
      field: 'storageLocation',
      type: 'string',
      headerName: 'Storage location',
      minWidth: 150,
      flex: 2,
    },
    {
      field: 'assaysOrdered',
      headerName: 'Assays ordered',
      minWidth: 150,
      flex: 3,
      renderCell: ({ row }) => (
        <AssaysOrderedCell
          row={row}
          dataToSubmit={dataToSubmit}
        />
      ),
    },
    {
      field: 'downstreamProcess',
      headerName: 'Downstream Process',
      minWidth: 150,
      flex: 1,
      renderCell: ({ row }) => row.downstreamProcess && renderAssayChip(row.downstreamProcess, row),
    },
    {
      field: 'dateSentForSequencing',
      headerName: 'Date sent for sequencing',
      minWidth: 125,
      flex: 1,
      renderCell: ({ row }) => {
        const date = row.dateSentForSequencing;
        return date ? (
          <CustomChip
            label={dayjs(date).format('DD/MM/YYYY')}
            fontWeight="bold"
            backgroundColour={corePalette.grey10}
            border={`1px solid ${corePalette.grey50}`}
          />
        ) : (
          null
        );
      },
    },
    {
      field: 'panelBuilderFiles',
      headerName: 'Files',
      maxWidth: 60,
      minWidth: 60,
      renderCell: ({ row }) => (row.panelBuilderFiles.length > 0 ? (
        <FilesButton files={row.panelBuilderFiles} entityName={row.name} />
      ) : <div />),
    },
  ], [
    canSelectBiomaterial,
    dataToSubmit,
    selectedBiomaterialIds,
    renderAssayChip,
    onToggle,
    search,
  ]);

  return (
    <CustomDataGrid
      columns={cols}
      rows={rows}
      hideCountSummary
      getCellSx={(row) => (
        rowMatchesSelected(row)
          ? {
            backgroundColor: corePalette.green10,
          }
          : {}
      )}
      getRowClassName={({ row }) => (rowMatchesSelected(row)
        ? 'selected-biomaterial-row'
        : ''
      )}
      sx={{
        '& .selected-biomaterial-row': {
          border: `1px solid ${corePalette.green100}`,
        },
      }}
    />
  );
}
